import { Component, HostListener, OnInit } from '@angular/core';
import { NavigationHelper } from '../../helpers/navigation';
import { Question } from '../../models/notes/question.model';
import { NoteService } from '../../services/note.service';
import { RandomQuestionService } from '../../services/random-question.service';
import { PottyNotesAlertController } from '../../../../src/app/error-handling/alerting/potty-notes-alert-controller';
import { Answer } from '../../../../src/app/models/notes/answer.model';
import { WouldYouRatherQuestion } from '../../interfaces/would-you-rather.interface';
import { animate, style, transition, trigger } from "@angular/animations";
import { Router } from '@angular/router';
import { PottyNotesGestureController } from '../../utils/gesture.controller';

@Component({
    selector: 'app-random-question',
    templateUrl: './random-question.component.html',
    styleUrls: ['./random-question.component.scss'],
    animations: [
        trigger('answerTransition', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('300ms ease-out', style({ opacity: 1 }))
            ]),
        ])
    ],
    standalone: false
})
export class RandomQuestionComponent implements OnInit {
	public static readonly ComponentIdentifier: number = 5;
	public nextButtonText: string;
	public activeStep = 6;
	public selectedQuestionId = '';
	public selectedAnswer = '';
	public currentIndex = 0;
	public rawQuestions: any;
	public wouldYouRatherQuestions: WouldYouRatherQuestion[]
	public isStartOfFlow: boolean;
	public displayedAnswers: Array<any>;

	private initialTouchX: number | null = null;
	private initialTouchY: number | null = null;
	private touchStartTime: number | null = null;

	constructor(private randomQuestionService: RandomQuestionService,
		private noteService: NoteService,
		private pottyNotesAlertController: PottyNotesAlertController,
		private navigationHelper: NavigationHelper,
		private router: Router) { }

	ngOnInit(): void {
		this.wouldYouRatherQuestions = new Array();
		this.displayedAnswers = new Array();
		this.isStartOfFlow = this.navigationHelper.isStartOfNoteFlow();
		this.nextButtonText = this.navigationHelper.isEndOfNoteFlow() ? 'Submit Note' : 'skip';

		if (this.noteService.cachedQuestions != null) {
			this.rawQuestions = this.noteService.cachedQuestions;
			this.wouldYouRatherQuestions = this.TransformToWouldYouRather(this.rawQuestions);

			if (this.noteService.selectedQuestionIndex  != null) {
				this.populatePreviouslyDisplayedQuestion(this.noteService.selectedQuestionIndex);
				this.currentIndex = this.noteService.selectedQuestionIndex;
			}

			if (this.noteService.selectedQuestionAnswer != null) {
				this.selectedAnswer = this.noteService.selectedQuestionAnswer;
				this.nextButtonText = 'next';
			}

			if (this.noteService.selectedQuestionId != null) {
				this.selectedQuestionId = this.noteService.selectedQuestionId;
			}
		}
		else {
			this.randomQuestionService.getRandomQuestions()
			.subscribe(
				(response) => {
					this.rawQuestions = response.questions;
					this.noteService.cachedQuestions = this.rawQuestions;
					this.wouldYouRatherQuestions = this.TransformToWouldYouRather(this.rawQuestions);
					this.populateDisplayedQuestion();
				},
				(error) => {
					this.pottyNotesAlertController.displayGetQuestionsFailure();
				}
			);
		}
	}

	public getAnswers(): string[] | null {
		if (this.wouldYouRatherQuestions.length == 0) return null;

		return Object.values(this.wouldYouRatherQuestions[this.currentIndex].answers);
	}

	public selectAnswer(answer: string): void {
		this.selectedAnswer = answer;
		this.selectedQuestionId = this.wouldYouRatherQuestions[this.currentIndex].questionId;
		this.nextButtonText = 'next';
		this.noteService.selectedQuestionAnswer = answer;
		this.noteService.selectedQuestionIndex = this.currentIndex;
		this.noteService.selectedQuestionId = this.wouldYouRatherQuestions[this.currentIndex].questionId;
	}

	public showNextQuestion(): void {
		if (this.selectedQuestionId && this.selectedAnswer) {
			this.selectedAnswer = '';
			this.selectedQuestionId = '';
			this.noteService.selectedQuestionAnswer = null;
			this.noteService.selectedQuestionIndex = null;
			this.nextButtonText = 'skip';
		}

		this.currentIndex++;
		this.noteService.selectedQuestionIndex = this.currentIndex;

		if (this.currentIndex > this.wouldYouRatherQuestions.length - 1) {
			this.currentIndex = 0;
			this.noteService.selectedQuestionIndex = 0;
		}

		this.populateDisplayedQuestion();
	}

	private populateDisplayedQuestion() {
		this.displayedAnswers = new Array();

		this.displayedAnswers.push(this.wouldYouRatherQuestions[this.currentIndex].answers.one);
		this.displayedAnswers.push(this.wouldYouRatherQuestions[this.currentIndex].answers.two);
	}

	private populatePreviouslyDisplayedQuestion(savedIndex: number): void {
		this.displayedAnswers = new Array();

		this.displayedAnswers.push(this.wouldYouRatherQuestions[savedIndex].answers.one);
		this.displayedAnswers.push(this.wouldYouRatherQuestions[savedIndex].answers.two);
	}

	public saveQuestionMetadata(event: Event): void {
		const wasSkipped = (event.type == 'touchmove' && this.selectedAnswer == '') || this.navigationHelper.skipButtonPressed(event);
		this.noteService.setAnswerForQuestion(new Answer(
			new Question(
				this.rawQuestions[this.currentIndex].questionId,
				this.rawQuestions[this.currentIndex].category,
				this.rawQuestions[this.currentIndex].answers),
			this.selectedAnswer), wasSkipped);
	}

	private TransformToWouldYouRather(questions: any): WouldYouRatherQuestion[] {
		let allQuestions: any;
		allQuestions = new Array<WouldYouRatherQuestion>();

		questions.forEach(element => {
			allQuestions.push({
				questionId: element.questionId,
				category: element.category,
				answers: {
					one: element.answers.substring(
						this.getPosition(element.answers, "\"", 3) + 1,
						this.getPosition(element.answers, "\"", 4)),
					two: element.answers.substring(
						this.getPosition(element.answers, "\"", 7) + 1,
						this.getPosition(element.answers, "\"", 8)),
				}
			} as WouldYouRatherQuestion)
		});

		return allQuestions as WouldYouRatherQuestion[];
	}

	private getPosition(string, subString, index) {
		return string.split(subString, index).join(subString).length;
	}

	// Gesture control 
	@HostListener('touchstart', ['$event'])
	public onTouchStart(event: TouchEvent): void {
		event.stopPropagation();
		this.initialTouchX = event.touches[0].clientX;
		this.initialTouchY = event.touches[0].clientY;
		this.touchStartTime = Date.now();
	}

	@HostListener('touchmove', ['$event'])
	public onTouchMove(event: TouchEvent): void {
		event.stopPropagation();

		if (this.initialTouchX === null || this.touchStartTime === null || this.initialTouchY == null) return;

		const currentTouchX = event.touches[0].clientX;
		const currentTouchY = event.touches[0].clientY;
		const deltaX = currentTouchX - this.initialTouchX;
		const deltaY = currentTouchY - this.initialTouchY;
		const timeDiff = Date.now() - this.touchStartTime

		// This represents moving backward in the add notes flow.
		if (PottyNotesGestureController.MovedRightEnough(timeDiff, deltaX, deltaY)) {
			this.saveQuestionMetadata(event);
			this.router.navigate([`${this.navigationHelper.getPreviousComponentUrl()}`]);
		}
		// Otherwise, forward.
		else if (PottyNotesGestureController.MovedLeftEnough(timeDiff, deltaX, deltaY)) {
			this.saveQuestionMetadata(event);
			this.router.navigate([`${this.navigationHelper.getNextComponentUrl()}`]);
		}
	}

	@HostListener('touchend', ['$event'])
	public onTouchEnd(event: TouchEvent): void {
		event.stopPropagation();
	}
}
